import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Package, Snowflake, AlertTriangle, Wine, Truck, Car,
  Pill, Boxes, ArrowRight, CheckCircle2, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const CargoTypesPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';

  const cargoTypes = [
    {
      id: 'general',
      icon: Package,
      name: isSpanish ? 'Carga General' : 'General Cargo',
      description: isSpanish
        ? 'Mercancías convencionales que no requieren manejo especial. Incluye productos manufacturados, maquinaria ligera y bienes de consumo.'
        : 'Conventional goods that do not require special handling. Includes manufactured products, light machinery, and consumer goods.',
      features: isSpanish
        ? ['Palletizado estándar', 'Contenedores secos', 'Embalaje flexible', 'Rutas globales']
        : ['Standard palletization', 'Dry containers', 'Flexible packaging', 'Global routes'],
      image: '/assets/sector-industrial.jpg',
      color: 'from-blue-500 to-blue-700'
    },
    {
      id: 'perishable',
      icon: Snowflake,
      name: isSpanish ? 'Productos Perecederos' : 'Perishable Goods',
      description: isSpanish
        ? 'Alimentos frescos, productos congelados y artículos sensibles a la temperatura que requieren cadena de frío controlada.'
        : 'Fresh food, frozen products, and temperature-sensitive items that require controlled cold chain.',
      features: isSpanish
        ? ['Cadena de frío -25°C a +25°C', 'Monitoreo en tiempo real', 'Contenedores reefer', 'Certificación alimentaria']
        : ['Cold chain -25°C to +25°C', 'Real-time monitoring', 'Reefer containers', 'Food certification'],
      image: '/assets/sector-coldchain.jpg',
      color: 'from-cyan-500 to-cyan-700'
    },
    {
      id: 'hazardous',
      icon: AlertTriangle,
      name: isSpanish ? 'Mercancías Peligrosas' : 'Hazardous Materials',
      description: isSpanish
        ? 'Productos químicos, inflamables y materiales regulados que requieren documentación y manejo especializado según normativas internacionales.'
        : 'Chemicals, flammables, and regulated materials requiring specialized documentation and handling per international regulations.',
      features: isSpanish
        ? ['Certificación IATA/IMDG', 'Personal capacitado', 'Documentación especial', 'Rutas autorizadas']
        : ['IATA/IMDG certification', 'Trained personnel', 'Special documentation', 'Authorized routes'],
      image: '/assets/banner-dangerous-cargo.jpg',
      color: 'from-orange-500 to-orange-700'
    },
    {
      id: 'fragile',
      icon: Wine,
      name: isSpanish ? 'Carga Frágil' : 'Fragile Cargo',
      description: isSpanish
        ? 'Artículos delicados como electrónicos, arte, vidrio y equipos sensibles que necesitan protección adicional durante el transporte.'
        : 'Delicate items such as electronics, art, glass, and sensitive equipment that need additional protection during transport.',
      features: isSpanish
        ? ['Embalaje especializado', 'Manejo con cuidado', 'Seguro premium', 'Rastreo dedicado']
        : ['Specialized packaging', 'Careful handling', 'Premium insurance', 'Dedicated tracking'],
      image: '/assets/sector-electronics.jpg',
      color: 'from-purple-500 to-purple-700'
    },
    {
      id: 'oversized',
      icon: Truck,
      name: isSpanish ? 'Carga Sobredimensionada' : 'Oversized Cargo',
      description: isSpanish
        ? 'Maquinaria pesada, equipos industriales y cargas que exceden dimensiones estándar requiriendo permisos y rutas especiales.'
        : 'Heavy machinery, industrial equipment, and loads exceeding standard dimensions requiring special permits and routes.',
      features: isSpanish
        ? ['Flat racks y open tops', 'Permisos especiales', 'Escoltas cuando necesario', 'Planificación de rutas']
        : ['Flat racks & open tops', 'Special permits', 'Escorts when needed', 'Route planning'],
      image: '/assets/sector-construction.jpg',
      color: 'from-amber-500 to-amber-700'
    },
    {
      id: 'vehicles',
      icon: Car,
      name: isSpanish ? 'Vehículos' : 'Vehicles',
      description: isSpanish
        ? 'Automóviles, motocicletas, equipos agrícolas y maquinaria rodante con servicios RoRo y contenedores especializados.'
        : 'Automobiles, motorcycles, agricultural equipment, and rolling machinery with RoRo services and specialized containers.',
      features: isSpanish
        ? ['Servicio RoRo', 'Contenedores dedicados', 'Seguro especializado', 'Inspección pre-embarque']
        : ['RoRo service', 'Dedicated containers', 'Specialized insurance', 'Pre-shipment inspection'],
      image: '/assets/sector-automotive.jpg',
      color: 'from-red-500 to-red-700'
    },
    {
      id: 'pharmaceutical',
      icon: Pill,
      name: isSpanish ? 'Farmacéuticos' : 'Pharmaceuticals',
      description: isSpanish
        ? 'Medicamentos, vacunas y productos de salud que requieren cumplimiento GDP y control estricto de temperatura.'
        : 'Medicines, vaccines, and health products requiring GDP compliance and strict temperature control.',
      features: isSpanish
        ? ['Cumplimiento GDP', 'Cadena de frío validada', 'Documentación completa', 'Trazabilidad total']
        : ['GDP compliance', 'Validated cold chain', 'Complete documentation', 'Full traceability'],
      image: '/assets/sector-coldchain.jpg',
      color: 'from-green-500 to-green-700'
    },
    {
      id: 'bulk',
      icon: Boxes,
      name: isSpanish ? 'Carga a Granel' : 'Bulk Cargo',
      description: isSpanish
        ? 'Granos, minerales, líquidos y materias primas transportadas sin embalaje individual en grandes volúmenes.'
        : 'Grains, minerals, liquids, and raw materials transported without individual packaging in large volumes.',
      features: isSpanish
        ? ['Buques graneleros', 'Tanques especializados', 'Manejo portuario', 'Contratos a largo plazo']
        : ['Bulk carriers', 'Specialized tanks', 'Port handling', 'Long-term contracts'],
      image: '/assets/sector-agricultural.jpg',
      color: 'from-yellow-500 to-yellow-700'
    }
  ];

  return (
    <div className="min-h-screen bg-steel-50" data-testid="cargo-types-page">
      {/* Hero Section */}
      <section className="bg-navy-900 pt-32 pb-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-gold/10 border border-gold/30 rounded-full text-gold text-sm font-medium tracking-wider uppercase mb-6">
              {isSpanish ? 'Soluciones por Tipo' : 'Solutions by Type'}
            </span>
            <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
              {isSpanish ? 'Tipos de Carga' : 'Cargo Types'}
            </h1>
            <p className="text-steel-300 text-lg mb-10">
              {isSpanish 
                ? 'Soluciones de transporte especializadas para cada tipo de mercancía. Desde carga general hasta productos que requieren manejo especial.'
                : 'Specialized transport solutions for every type of goods. From general cargo to products requiring special handling.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote">
                <Button className="btn-accent h-14 px-8 text-base">
                  {isSpanish ? 'Solicitar Cotización' : 'Request Quote'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cargo Types Grid */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {cargoTypes.map((cargo, index) => (
              <motion.div
                key={cargo.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`cargo-card-${cargo.id}`}
              >
                <Card className="h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group cursor-pointer">
                  {/* Image Header */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={cargo.image}
                      alt={cargo.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t ${cargo.color} opacity-70`} />
                    
                    {/* Icon */}
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg">
                      <cargo.icon className="w-6 h-6 text-navy-800" />
                    </div>
                    
                    {/* Title */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-outfit text-lg font-bold text-white drop-shadow-lg">
                        {cargo.name}
                      </h3>
                    </div>
                  </div>

                  <CardContent className="p-5">
                    <p className="text-steel-600 text-sm mb-4 line-clamp-3">
                      {cargo.description}
                    </p>
                    
                    {/* Features */}
                    <ul className="space-y-2 mb-4">
                      {cargo.features.slice(0, 3).map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-xs text-steel-600">
                          <CheckCircle2 className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* CTA */}
                    <Link 
                      to="/quote" 
                      className="inline-flex items-center gap-1 text-sm font-semibold text-navy-800 hover:text-gold transition-colors"
                    >
                      {isSpanish ? 'Cotizar' : 'Get Quote'}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-navy-900">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
                {isSpanish 
                  ? '¿Tiene un Tipo de Carga Especial?' 
                  : 'Have a Special Cargo Type?'}
              </h2>
              <p className="text-steel-300 text-lg mb-10">
                {isSpanish
                  ? 'Nuestros expertos pueden diseñar una solución personalizada para cualquier tipo de mercancía.'
                  : 'Our experts can design a customized solution for any type of goods.'}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button className="btn-accent h-14 text-base px-10">
                    {isSpanish ? 'Hablar con un Experto' : 'Talk to an Expert'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CargoTypesPage;
